import Head from 'next/head';
import { useEffect, useMemo, useState } from 'react';
import Footer from '@/website/components/Footer';
import Header from '@/website/components/Header';
import { LegalDocumentVersions, LegalPagePost } from '@/website/util/legal';
import { useLocalTranslation } from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';
import LegalDocumentContent from './LegalDocumentContent';
import LegalDocumentToc from './LegalDocumentToc';
import LegalVersionSwitcher, { versionLabelKeys } from './LegalVersionSwitcher';
import { parseLegalDocument } from './utils';

interface LegalDocumentViewProps {
  post: LegalPagePost;
  documentVersions: LegalDocumentVersions | null;
}

export default function LegalDocumentView(props: LegalDocumentViewProps): JSX.Element {
  const { post, documentVersions } = props;

  /**
   * state
   */
  const { $t } = useLocalTranslation();
  const model = useMemo(() => (post.document ? parseLegalDocument(post.document) : { blocks: [], toc: [] }), [post.document]);
  const [activeSectionId, setActiveSectionId] = useState('');
  // The version part of the title comes from the active version (the ?version= param), so it stays out of the translated data files.
  const titleVersionSuffix = useMemo(() => (documentVersions ? ` (${$t(versionLabelKeys[documentVersions.active])})` : ''), [documentVersions, $t]);

  /**
   * effects
   */
  useEffect(() => {
    const headings = model.toc.map((item) => document.getElementById(item.id)).filter((element): element is HTMLElement => Boolean(element));
    if (headings.length === 0) return;

    setActiveSectionId(headings[0].id);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveSectionId(visible[0].target.id);
        }
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: [0, 1] }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [model]);

  /**
   * methods
   */
  const scrollToSectionAction = (id: string) => {
    setActiveSectionId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <Box className="mx-auto flex w-full flex-col">
      <Head>
        <title>{post.title} | Jove Insurance</title>
      </Head>
      <Header />
      <Box className="bg-light-blue">
        <Box className="mx-auto max-w-7xl px-4 py-10 md:py-14">
          <Typography component="h1" className="text-true-black max-w-3xl font-serif text-3xl font-black leading-tight md:text-5xl">
            {post.title}
            {titleVersionSuffix}
          </Typography>
          {documentVersions && <LegalVersionSwitcher documentVersions={documentVersions} />}
        </Box>
      </Box>
      {post.pdfPath ? (
        <Box className="mx-auto w-full max-w-7xl px-4 py-10">
          <Box component="iframe" src={post.pdfPath} title={post.title} className="border-grey-10 h-screen w-full rounded-lg border" />
        </Box>
      ) : (
        <Box className="mx-auto flex w-full max-w-7xl flex-col px-4 py-10 md:flex-row md:gap-12">
          <LegalDocumentToc items={model.toc} activeId={activeSectionId} onSelect={scrollToSectionAction} />
          <Box className="min-w-0 flex-1">
            <LegalDocumentContent blocks={model.blocks} />
          </Box>
        </Box>
      )}
      <Footer />
    </Box>
  );
}
